import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Axios } from "../../../Api/axios";
import {
  ADMIN_GET_STUDENT,
  ADMIN_UPDATE_STUDENT,
  ADMIN_ALL_CLASSES,
  SUBJECTS_BY_CLASS,
  baseURL,
} from "../../../Api/Api";
import LoadingSubmit from "../../../Components/Loading/Loading";
import DashDialog from "../../../Components/Dashboard/DashDialog";
import { useNavigate, useParams } from "react-router-dom";

export default function Student() {
  const { id } = useParams();
  const nav    = useNavigate();

  const [pageLoading, setPageLoading] = useState(true);

  // form fields
  const [name,     setName]     = useState("");
  const [phone,    setPhone]    = useState("");
  const [year,     setYear]     = useState("");
  const [vegSystem, setVegSystem] = useState("");   // "class" | "subject"
  const [photo,    setPhoto]    = useState(null);
  const [currentPhoto, setCurrentPhoto] = useState(null);

  // class / subject selectors
  const [classes,          setClasses]          = useState([]);
  const [selectedGradeId,  setSelectedGradeId]  = useState("");
  const [subjects,         setSubjects]         = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loadingSubjects,  setLoadingSubjects]  = useState(false);

  const [photoCleared, setPhotoCleared] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const nameRef = useRef(null);

  // ── load student + classes in parallel ──────────────────────────────
  useEffect(() => {
    Promise.all([
      Axios.get(ADMIN_GET_STUDENT(id)),
      Axios.get(ADMIN_ALL_CLASSES),
    ])
      .then(([stuRes, clsRes]) => {
        const s = stuRes.data.student;
        setName(s.name  || "");
        setPhone(s.phone || "");
        setYear(s.year  || "");
        setCurrentPhoto(s.photo || null);
        setClasses(clsRes.data.classes || clsRes.data || []);

        if (s.class_id) setSelectedGradeId(String(s.class_id));

        // derive system from registration_system field
        if (s.registration_system === "نظام مواد") {
          setVegSystem("subject");
          // pre-select enrolled subjects
          const enrolled = stuRes.data.student.subjects?.map((sub) => sub.id) || [];
          setSelectedSubjects(enrolled);
        } else {
          setVegSystem("class");
        }
      })
      .catch(() => nav("/dashboard/users", { replace: true }))
      .finally(() => {
        setPageLoading(false);
        nameRef.current?.focus();
      });
  }, [id, nav]);

  // ── load subjects when class or system changes ───────────────────────
  useEffect(() => {
    if (!selectedGradeId || vegSystem !== "subject") {
      setSubjects([]);
      return;
    }
    setLoadingSubjects(true);
    Axios.get(SUBJECTS_BY_CLASS(selectedGradeId))
      .then((res) => setSubjects(res.data.subjects || []))
      .catch(() => setSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [selectedGradeId, vegSystem]);

  // ── submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      const regSystem = vegSystem === "class" ? "نظام صفي" : "نظام مواد";
      const fd = new FormData();
      fd.append("name",                name.trim());
      fd.append("phone",               phone.trim());
      fd.append("registration_system", regSystem);
      fd.append("class_id",            selectedGradeId);
      if (year) fd.append("year", year);
      if (photo) fd.append("photo", photo);
      else if (photoCleared) fd.append("remove_photo", "true");
      if (vegSystem === "subject") {
        fd.append("subjectIds", JSON.stringify(selectedSubjects));
      }
      await Axios.put(ADMIN_UPDATE_STUDENT(id), fd);
      nav("/dashboard/users");
    } catch (err) {
      setSubmitLoading(false);
      console.error(err);
    }
  };

  const isValid =
    name.trim().length > 1 &&
    phone.trim().length > 5 &&
    !!selectedGradeId &&
    !!vegSystem &&
    (vegSystem !== "subject" || selectedSubjects.length > 0);

  const photoPreview = photo
    ? URL.createObjectURL(photo)
    : !photoCleared && currentPhoto
    ? `${baseURL}/uploads/students/${currentPhoto}`
    : null;

  if (pageLoading) return <LoadingSubmit />;

  return (
    <>
      {submitLoading && <LoadingSubmit />}
      <DashDialog
        title="تعديل بيانات الطالب"
        onSubmit={handleSubmit}
        submitDisabled={!isValid || submitLoading}
        submitLabel={submitLoading ? "جاري الحفظ..." : "حفظ التعديلات"}
      >
        {/* Photo */}
        <Form.Group className="mb-4 text-center">
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              margin: "0 auto 10px",
              overflow: "hidden",
              background: "linear-gradient(135deg,#00a74f,#007a38)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid #e5e7eb",
            }}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="صورة" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <svg width="36" height="36" fill="#fff" viewBox="0 0 24 24">
                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
              </svg>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <Form.Label
              htmlFor="photoInput"
              style={{
                fontSize: "0.8rem",
                color: "#00a74f",
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
                margin: 0,
              }}
            >
              {(currentPhoto && !photoCleared) || photo ? "تغيير الصورة" : "رفع صورة"}
            </Form.Label>
            {((currentPhoto && !photoCleared) || photo) && (
              <button
                type="button"
                onClick={() => { setPhoto(null); setPhotoCleared(true); }}
                style={{
                  fontSize: "0.78rem",
                  color: "#dc2626",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  textDecoration: "underline",
                  padding: 0,
                }}
              >
                حذف الصورة
              </button>
            )}
          </div>
          <Form.Control
            id="photoInput"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => { setPhoto(e.target.files[0] || null); setPhotoCleared(false); }}
          />
        </Form.Group>

        {/* Class */}
        <Form.Group className="mb-4" controlId="gradeSelect">
          <Form.Label>الصف</Form.Label>
          <Form.Select
            value={selectedGradeId}
            onChange={(e) => setSelectedGradeId(e.target.value)}
          >
            <option value="" disabled>اختر الصف</option>
            {classes.map((g) => (
              <option key={g.id} value={String(g.id)}>{g.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Name */}
        <Form.Group className="mb-4" controlId="studentName">
          <Form.Label>اسم الطالب</Form.Label>
          <Form.Control
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            placeholder="الاسم..."
            required
          />
        </Form.Group>

        {/* Phone */}
        <Form.Group className="mb-4" controlId="phone">
          <Form.Label>رقم الهاتف</Form.Label>
          <Form.Control
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
            placeholder="0933..."
            required
            style={{ direction: "ltr" }}
          />
        </Form.Group>

        {/* Year */}
        <Form.Group className="mb-4" controlId="year">
          <Form.Label>السنة الدراسية</Form.Label>
          <Form.Control
            value={year}
            onChange={(e) => setYear(e.target.value)}
            type="text"
            placeholder="2026"
          />
        </Form.Group>

        {/* Registration system */}
        <Form.Group className="mb-4">
          <Form.Label>نوع التسجيل</Form.Label>
          <div style={{ display: "flex", gap: "20px" }}>
            <Form.Check
              label="نظام صفي"
              name="vegSystem"
              type="radio"
              value="class"
              checked={vegSystem === "class"}
              onChange={(e) => setVegSystem(e.target.value)}
            />
            <Form.Check
              label="نظام مواد"
              name="vegSystem"
              type="radio"
              value="subject"
              checked={vegSystem === "subject"}
              onChange={(e) => setVegSystem(e.target.value)}
            />
          </div>
        </Form.Group>

        {/* Subject picker (نظام مواد only) */}
        {vegSystem === "subject" && selectedGradeId && (
          <div className="mb-2">
            <Form.Label>المواد المختارة</Form.Label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                padding: "12px",
                background: "#f9fafb",
                borderRadius: "10px",
                minHeight: "56px",
                border: "1.5px solid #e5e7eb",
                marginBottom: "14px",
              }}
            >
              {selectedSubjects.length === 0 ? (
                <span style={{ color: "#9ca3af", fontSize: "0.85rem", alignSelf: "center" }}>
                  لم يتم اختيار مواد بعد
                </span>
              ) : (
                selectedSubjects.map((subId) => {
                  const sub = subjects.find((s) => s.id === subId);
                  return sub ? (
                    <span
                      key={sub.id}
                      onClick={() =>
                        setSelectedSubjects((p) => p.filter((x) => x !== sub.id))
                      }
                      style={{
                        background: "linear-gradient(135deg,#00a74f,#007a38)",
                        color: "#fff",
                        borderRadius: "50px",
                        padding: "4px 14px",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {sub.name} <span>×</span>
                    </span>
                  ) : null;
                })
              )}
            </div>

            <Form.Label>المواد المتاحة</Form.Label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {loadingSubjects ? (
                <span style={{ color: "#6b7280", fontSize: "0.85rem" }}>جاري التحميل...</span>
              ) : (
                subjects
                  .filter((s) => !selectedSubjects.includes(s.id))
                  .map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => setSelectedSubjects((p) => [...p, sub.id])}
                      style={{
                        border: "1.5px solid #00a74f",
                        borderRadius: "50px",
                        padding: "5px 16px",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "#00a74f",
                        background: "#f0fdf4",
                        cursor: "pointer",
                      }}
                    >
                      {sub.name}
                    </button>
                  ))
              )}
            </div>
          </div>
        )}
      </DashDialog>
    </>
  );
}
